import "./SchedulerEvent.scss"
import IconPin from "assets/img/icon-pin.svg?react"
import IconComment from "assets/img/icon-comment-dot.svg?react"
import IconTriangleDown from "assets/img/icon-triangle-down.svg?react"

import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Dayjs } from "utils/dayjs"
import cn from "classnames"


import { Flex, FlexItem } from "components/Flex"
import Text from "components/Text/Text"
import { EventType } from "config/constants"
import Tooltip from "components/Tooltip/Tooltip"
import AssetInspections from "components/AssetInspections/AssetInspections"
import Button from "components/Button/Button"
import { Guid, IApiLink } from "@/types"
import useReservationShippingOrders from "hooks/useReservationShippingOrders"
import { useAssetInspectionPairs } from "hooks/useAssetInspectionPairs"
import ReservationStatusTag from "components/ReservationStatusTag/ReservationStatusTag"
import { ReservationStatus } from "models/Reservation"
import useResizeObserver from "use-resize-observer"

export interface ISchedulerEventProps {
    assetId?: Guid
    reservationId?: Guid
    type: EventType
    title?: ReactNode
    descr?: string
    startDate?: Dayjs
    endDate?: Dayjs
    isCutStart?: boolean
    isCutEnd?: boolean
    progress?: number
    nowLine?: boolean
    isLocked?: boolean
    latestCondition?: number
    latestFuel?: number
    latestOperationHours?: number
    latestMileage?: number
    latestAdBlue?: number
    forceInspectionExpanded?: boolean
    linkGetReservationShippingOrders?: IApiLink
    isMachine?: boolean
    comment?: string
    status?: ReservationStatus
    onInspectionCreated?: () => void
    onInspectionExportClick?: (deliveryInspectionId?: Guid, returnInspectionId?: Guid) => Promise<void>
    onClick?: () => Promise<any> | any
}

const SchedulerEvent: FC<React.PropsWithChildren<ISchedulerEventProps>> = ({
    assetId,
    reservationId,
    type,
    title,
    descr,
    startDate,
    endDate,
    isCutStart,
    isCutEnd,
    progress,
    nowLine,
    isLocked,
    latestCondition,
    latestFuel,
    latestOperationHours,
    latestMileage,
    latestAdBlue,
    forceInspectionExpanded = false,
    linkGetReservationShippingOrders,
    isMachine,
    comment,
    status,
    onInspectionCreated,
    onInspectionExportClick,
    onClick
}) => {
    const { t } = useTranslation()
    const [inspectionExpanded, setInspectionExpanded] = useState(forceInspectionExpanded)

    // Fetch inspection pairs for this asset
    const { inspectionPairs, refetch: refetchInspectionPairs } = useAssetInspectionPairs({
        assetId: assetId,
        reservationId: reservationId
    })

    // Get delivery and return inspections from any pair for backward compatibility with shipping order logic
    const anyDeliveryInspection = useMemo(() => {
        return inspectionPairs?.find(pair => pair.deliveryInspection)?.deliveryInspection
    }, [inspectionPairs])

    const anyReturnInspection = useMemo(() => {
        return inspectionPairs?.find(pair => pair.returnInspection)?.returnInspection
    }, [inspectionPairs])

    // Keep the old variable names for compatibility with existing shipping order logic
    const deliveryInspection = anyDeliveryInspection
    const returnInspection = anyReturnInspection

    // Debug: Check if we should fetch shipping orders
    const shouldFetchShippingOrders = !((deliveryInspection && returnInspection) || returnInspection)
    const linkToUse = shouldFetchShippingOrders ? linkGetReservationShippingOrders : undefined

    const {
        refetchShippingOrders,
    } = useReservationShippingOrders({
        linkGetShippingOrders: linkToUse,
    })

    // Handle inspection creation - refetch data and call parent callback
    const handleInspectionCreated = useCallback(async () => {
        await refetchInspectionPairs()
        refetchShippingOrders()
        if (onInspectionCreated) {
            onInspectionCreated()
        }
    }, [refetchInspectionPairs, refetchShippingOrders, onInspectionCreated])

    // Check if any inspection pairs need inspections
    const hasRequiredInspections = useMemo(() => {
        if (!inspectionPairs) return false
        return inspectionPairs.some(pair => {
            const needsDeliveryInspection = !pair.deliveryInspection && (pair.deliveryInspectionId === null || pair.deliveryInspectionRequired)
            const needsReturnInspection = !pair.returnInspection && (pair.returnInspectionId === null || pair.returnInspectionRequired)
            return needsDeliveryInspection || needsReturnInspection
        })
    }, [inspectionPairs])

    const isInspectionExpanded = (!!deliveryInspection || !!returnInspection || hasRequiredInspections) && inspectionExpanded

    useEffect(() => {
        if (forceInspectionExpanded) {
            setInspectionExpanded(true)
        }
    }, [forceInspectionExpanded])


    const eventBody = (
        <div className={cn([
            "scheduler-event__body",
            `scheduler-event__body--type-${type}`,
            {
                "scheduler-event__body--cut-start": isCutStart,
                "scheduler-event__body--cut-end": isCutEnd,
            }
        ])}>
            <div
                className={cn([
                    "scheduler-event__inner",
                    `scheduler-event__inner--type-${type}`,
                ])}>
                <Flex
                    className="h-100"
                    flow="column"
                    justifyContent="space-between">
                    <FlexItem>
                        <Flex
                            justifyContent="space-between"
                            alignItems="center">
                            <FlexItem className="scheduler-event__title">
                                {title}
                            </FlexItem>
                            {!!startDate && type !== EventType.notAvailableAsset &&
                                <FlexItem className="max-w-100">
                                    <div className="scheduler-event__date" title={
                                        endDate ?
                                            `${startDate.format(t('config.dateLongTimeFormat'))} – ${endDate.format(t('config.dateLongTimeFormat'))}` :
                                            `${t('scheduler.fromX', { date: startDate.format(t('config.dateLongTimeFormat')) })}`
                                    }>
                                        {!endDate && (type === EventType.disposal ? startDate.format(t('config.dateLongFormat')) : `${t('scheduler.fromX', { date: startDate.format(t('config.dateDayMonthFormat')) })}`)}
                                        {!!endDate && `${startDate.format(t('config.dateDayMonthFormat'))} – ${endDate.format(t('config.dateDayMonthFormat'))}`}
                                    </div>
                                </FlexItem>
                            }
                        </Flex>
                    </FlexItem>
                    <FlexItem>
                        <Flex
                            justifyContent="space-between"
                            alignItems="center">
                            <FlexItem className="scheduler-event__descr">{descr}</FlexItem>
                            <FlexItem shrink={0} className="scheduler-event__actions">
                                <Flex alignItems="center" gap="0.8rem">
                                    {!!(deliveryInspection || returnInspection || hasRequiredInspections) && !forceInspectionExpanded &&
                                        <FlexItem shrink={0}>
                                            <Button
                                                theme="link"
                                                label={isInspectionExpanded ? t('assetInspection.hideInspection') : t('assetInspection.showInspection')}
                                                icon={<IconTriangleDown className={cn("transition", { "transform-rotate--180deg": !isInspectionExpanded })} />}
                                                iconPosition="end"
                                                size="small"
                                                onClick={(e) => { e.stopPropagation(); setInspectionExpanded(curentInspectionExpanded => !curentInspectionExpanded) }}
                                            />
                                        </FlexItem>
                                    }
                                    {comment &&
                                        <FlexItem>
                                            <Tooltip
                                                maxWidth="565px"
                                                whiteSpace="pre-wrap"
                                                content={<Text>{comment}</Text>}>
                                                <IconComment width={20} height={20} className="d-block"/>
                                            </Tooltip>
                                        </FlexItem>
                                    }
                                    {isLocked &&
                                        <FlexItem shrink={0}>
                                            <Tooltip
                                                maxWidth="565px"
                                                content={<Text>{t('scheduler.eventLockTooltip')}</Text>}>
                                                <IconPin width={16} height={16} className="d-block" />
                                            </Tooltip>
                                        </FlexItem>
                                    }
                                    {!!status &&
                                        <FlexItem>
                                            <ReservationStatusTag status={status} compact={true} />
                                        </FlexItem>
                                    }
                                </Flex>
                            </FlexItem>
                        </Flex>
                    </FlexItem>
                </Flex>
            </div>
        </div>
    )

    const eventContent = (
        <>
            <div
                className={cn(
                    "scheduler-event",
                    `scheduler-event--type-${type}`,
                    { "scheduler-event--inspection-expanded": isInspectionExpanded },
                )}
                onClick={onClick}
                data-testid="scheduler-event">
                {eventBody}
                {nowLine && typeof progress === "number" && progress > 0 && progress < 100 &&
                    <div className="scheduler-event__now" style={{ left: `${progress}%` }}></div>
                }
            </div>
            {isInspectionExpanded &&
                <AssetInspections
                    assetId={assetId}
                    inspectionPairs={inspectionPairs}
                    latestCondition={latestCondition}
                    latestFuel={latestFuel}
                    latestOperationHours={latestOperationHours}
                    latestMileage={latestMileage}
                    latestAdBlue={latestAdBlue}
                    onInspectionCreated={handleInspectionCreated}
                    isMachine={isMachine}
                    onInspectionExportClick={onInspectionExportClick}
                />
            }
        </>
    )

    const { ref: eventContentWrapEl, width: eventWidth = 1 } = useResizeObserver<HTMLDivElement>();
    const eventConventWrap = (
        <div ref={eventContentWrapEl}>
            {eventContent}
        </div>
    )

    if (eventWidth < 300
        && (type === EventType.reservation
            || type === EventType.relocation
            || type === EventType.sale)) {
        return (
            <Tooltip trigger="hover" theme="light" contentPadding={10} content={<>{eventContent}</>} delayShow={1000}>
                {eventConventWrap}
            </Tooltip>
        )
    }

    return eventContent
}

export default SchedulerEvent
